import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Small deterministic HSL color hashed from an email so each teammate gets
 *  a consistent chip colour without any config. */
export function colorForEmail(email) {
  const s = String(email || "").toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return {
    bg: `hsl(${hue} 65% 88%)`,
    border: `hsl(${hue} 45% 60%)`,
    text: `hsl(${hue} 60% 25%)`,
  };
}

function initialsFor(email, name) {
  const src = (name || "").trim() || (email || "").split("@")[0] || "?";
  const parts = src.replace(/[._-]+/g, " ").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Compact circular chip for a single teammate. */
export function AssigneeChip({ email, name, size = 26, title }) {
  const { bg, border, text } = colorForEmail(email);
  return (
    <span
      title={title || name || email}
      data-testid={`assignee-chip-${email}`}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: size, height: size, borderRadius: 999,
        background: bg, color: text, border: `1.5px solid ${border}`,
        fontFamily: "Host Grotesk, sans-serif", fontSize: Math.max(9, size * 0.42),
        fontWeight: 700, letterSpacing: 0.2,
        flexShrink: 0,
      }}
    >
      {initialsFor(email, name)}
    </span>
  );
}

/** Row of chips + an "Unassigned" placeholder when empty. */
export function AssigneeChipRow({ assignees = [], directory = {}, size = 24, maxVisible = 4 }) {
  if (!assignees || assignees.length === 0) {
    return (
      <span
        data-testid="assignee-unassigned"
        style={{ fontSize: 11.5, color: "var(--folk-hint)", fontStyle: "italic", fontFamily: "Host Grotesk, sans-serif" }}
      >
        unassigned
      </span>
    );
  }
  const visible = assignees.slice(0, maxVisible);
  const rest = Math.max(0, assignees.length - maxVisible);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      {visible.map((email) => (
        <AssigneeChip key={email} email={email} name={directory[email]?.name} size={size} />
      ))}
      {rest > 0 && (
        <span style={{ fontSize: 11, color: "var(--folk-muted)", fontFamily: "Host Grotesk, sans-serif", marginLeft: 2 }}>
          +{rest}
        </span>
      )}
    </span>
  );
}

/** Popover-style picker used inside the workflow page. */
export function AssigneeEditor({ submissionId, value = [], onChanged }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState(value);
  const ref = useRef(null);

  useEffect(() => { setLocal(value); }, [value]);

  useEffect(() => {
    axios
      .get(`${API}/auth/users`, { withCredentials: true })
      .then((r) => setMembers(r.data || []))
      .catch(() => setMembers([]));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (email) => {
    setLocal((cur) => cur.includes(email) ? cur.filter((e) => e !== email) : [...cur, email]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/survey/responses/${submissionId}/assignees`,
        { assignees: local },
        { withCredentials: true },
      );
      onChanged && onChanged(res.data?.assignees || []);
      toast.success(local.length ? "Assignees updated." : "Assignees cleared.");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save assignees.");
    } finally { setSaving(false); }
  };

  const directory = Object.fromEntries(members.map((m) => [m.email, m]));

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8 }}>
      <AssigneeChipRow assignees={value} directory={directory} size={26} />
      <button
        type="button"
        data-testid="assignee-editor-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent", border: "1.5px solid var(--folk-border)",
          borderRadius: 999, padding: "5px 12px", fontSize: 12,
          color: "var(--folk-accent)", cursor: "pointer",
          fontFamily: "Host Grotesk, sans-serif",
        }}
      >
        {value.length ? "Change" : "Assign"}
      </button>
      {open && (
        <div
          data-testid="assignee-editor-panel"
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 20,
            background: "#FFF", border: "1.5px solid var(--folk-border)", borderRadius: 14,
            boxShadow: "0 12px 32px rgba(27, 26, 26, 0.12)",
            width: 260, padding: 12,
            display: "flex", flexDirection: "column", gap: 8,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Assign to</div>
          {members.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--folk-muted)" }}>No teammates yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
              {members.map((m) => {
                const active = local.includes(m.email);
                return (
                  <label
                    key={m.email}
                    data-testid={`assignee-option-${m.email}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "6px 8px", borderRadius: 8, cursor: "pointer",
                      background: active ? "#FFFBC9" : "transparent",
                      fontFamily: "Host Grotesk, sans-serif", fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(m.email)}
                      style={{ width: 16, height: 16 }}
                    />
                    <AssigneeChip email={m.email} name={m.name} size={22} />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {m.name || m.email}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => { setLocal([]); }}
              disabled={saving}
              style={{ background: "transparent", border: "none", color: "var(--folk-muted)", cursor: "pointer", fontSize: 12, textDecoration: "underline", fontFamily: "Host Grotesk, sans-serif" }}
            >
              Clear
            </button>
            <button
              type="button"
              data-testid="assignee-editor-save"
              onClick={save}
              disabled={saving}
              className="folk-pill"
              style={{ padding: "6px 14px", fontSize: 12, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", opacity: saving ? 0.55 : 1 }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
